/** the projects slice's reducer. See `store.ts`. */
import { createReducer } from '@reduxjs/toolkit';
import type { Draft } from 'immer';
import type { CombinedAssetSummary } from './combineServices';

import {
  COMBINE_PROJECT_FAILURE,
  DELETE_PROJECT_SUCCESS,
  CREATE_PROJECT_FAILURE,
  CREATE_PROJECT_REQUEST,
  CREATE_PROJECT_SUCCESS,
  LOAD_COMBINED_FAILURE,
  LOAD_COMBINED_REQUEST,
  SET_PROJECT_BATES_REQUEST,
  CLEAR_PROJECT_BATES_REQUEST,
  CLEAR_PROJECT_BATES_SUCCESS,
  LOAD_PROJECT_REDACTION_FAILURE,
  LOAD_PROJECT_REDACTION_SUCCESS,
  SET_PROJECT_WATERMARKS_FAILURE,
  SET_PROJECT_WATERMARKS_REQUEST,
  SET_PROJECT_WATERMARKS_SUCCESS,
  SET_PROJECT_BATES_SUCCESS,
  SET_PROJECT_BATES_FAILURE,
  LOAD_COMBINED_SUCCESS,
  RENAME_PROJECT_FAILURE,
  RENAME_PROJECT_REQUEST,
  RENAME_PROJECT_SUCCESS,
  SET_COMBINED_PREVIEW,
  COMBINE_PROJECT_REQUEST,
  COMBINE_PROJECT_SUCCESS,
  COMBINE_PROJECT_VERSION,
  LOAD_PROJECT_FAILURE,
  LOAD_PROJECT_REQUEST,
  LOAD_PROJECT_SUCCESS,
  UPDATE_PROJECT_FAILURE,
  UPDATE_PROJECT_REQUEST,
  UPDATE_PROJECT_SUCCESS,
} from './actions';
import { buildMergedDocumentFilename } from '../documents/filename';
import { isSameBatesSettings } from './batesStamp';
import { defaultState } from './store';

export const projectsReducer = createReducer(defaultState, (builder) =>
  builder
    .addCase(LOAD_PROJECT_REQUEST, (state, action) => {
      state.loading[action.payload.projectTdoId] = true;
    })
    .addCase(LOAD_PROJECT_SUCCESS, (state, action) => {
      state.membersByProject[action.payload.projectTdoId] = [
        ...action.payload.members,
      ];
      if (action.payload.modifiedDateTime) {
        state.membershipDecidedAt[action.payload.projectTdoId] =
          action.payload.modifiedDateTime;
      }
      delete state.loading[action.payload.projectTdoId];
    })
    .addCase(LOAD_PROJECT_FAILURE, (state, action) => {
      // The cached members are LEFT in place: a failed refresh is not evidence
      // the membership changed, and dropping them would send the panel back to
      // "everything included", which is a claim rather than an absence.
      delete state.loading[action.payload.projectTdoId];
    })
    .addCase(CREATE_PROJECT_REQUEST, (state, action) => {
      state.creating[action.payload.caseId] = true;
    })
    .addCase(CREATE_PROJECT_SUCCESS, (state, action) => {
      delete state.creating[action.payload.caseId];
    })
    .addCase(CREATE_PROJECT_FAILURE, (state, action) => {
      delete state.creating[action.payload.caseId];
    })
    .addCase(UPDATE_PROJECT_REQUEST, (state, action) => {
      state.saving[action.payload.projectTdoId] = true;
    })
    .addCase(UPDATE_PROJECT_SUCCESS, (state, action) => {
      // Keep the cache honest after our own write, so leaving edit mode shows
      // what was just saved without a refetch.
      state.membersByProject[action.payload.projectTdoId] = [
        ...action.payload.members,
      ];
      // AND THE DECISION, AS THE RECORD HOLDS IT. Without mirroring it, every
      // file the reviewer just declined would still count as "arrived since
      // the last decision" and the offer to add them would come straight
      // back.
      //
      // CARRIED, NOT RE-MINTED. This used to call `new Date()` here while
      // `updateProjectMembers` called it again for the record — two readings
      // of one clock, differing by the round trip, so the store and the
      // record disagreed about the same fact from the moment of the write.
      if (action.payload.decidedAt) {
        state.membershipDecidedAt[action.payload.projectTdoId] =
          action.payload.decidedAt;
      }
      delete state.saving[action.payload.projectTdoId];
    })
    .addCase(UPDATE_PROJECT_FAILURE, (state, action) => {
      delete state.saving[action.payload.projectTdoId];
    })
    .addCase(COMBINE_PROJECT_REQUEST, (state, action) => {
      state.combining[action.payload.projectTdoId] = true;
    })
    .addCase(COMBINE_PROJECT_VERSION, (state, action) => {
      state.combiningVersion[action.payload.projectTdoId] =
        action.payload.version;
    })
    .addCase(COMBINE_PROJECT_SUCCESS, (state, action) => {
      delete state.combining[action.payload.projectTdoId];
      delete state.combiningVersion[action.payload.projectTdoId];
    })
    .addCase(COMBINE_PROJECT_FAILURE, (state, action) => {
      delete state.combining[action.payload.projectTdoId];
      delete state.combiningVersion[action.payload.projectTdoId];
    })
    .addCase(SET_COMBINED_PREVIEW, (state, action) => {
      state.previewingCombinedFor = action.payload.projectTdoId
        ? String(action.payload.projectTdoId)
        : null;
    })
    .addCase(RENAME_PROJECT_REQUEST, (state, action) => {
      /**
       * OPTIMISTIC, because the write is slow and the control is not.
       */
      const id = action.payload.projectTdoId;
      state.renameRollback[id] = {
        name: state.nameByProject[id],
        options: state.filenameOptionsByProject[id]
          ? { ...state.filenameOptionsByProject[id] }
          : undefined,
        assetName: state.combinedByProject[id]?.name ?? undefined,
      };
      state.nameByProject[id] = action.payload.name.trim();
      state.filenameOptionsByProject[id] = {
        ...action.payload.filenameOptions,
      };
      /**
       * AND THE ASSET'S FILENAME WITH IT.
       */
      const asset = state.combinedByProject[id];
      if (asset) {
        const issued = new Date(asset.createdDateTime);
        asset.name = buildMergedDocumentFilename(
          action.payload.name.trim(),
          {
            ...action.payload.filenameOptions,
            hasRedactions: asset.hasRedactions,
            version: asset.version,
          },
          Number.isNaN(issued.getTime()) ? new Date() : issued
        );
      }
    })
    .addCase(RENAME_PROJECT_SUCCESS, (state, action) => {
      delete state.renameRollback[action.payload.projectTdoId];
      // Confirmation, not news — the optimistic write above already showed
      // this. Kept because the saga's value is the authoritative one and a
      // concurrent load could have landed in between.
      state.nameByProject[action.payload.projectTdoId] = action.payload.name;
      state.filenameOptionsByProject[action.payload.projectTdoId] = {
        ...action.payload.filenameOptions,
      };
    })
    .addCase(RENAME_PROJECT_FAILURE, (state, action) => {
      /**
       * BACK TO WHAT THE REDUCER SAW, including the asset's filename.
       */
      const id = action.payload.projectTdoId;
      const previous = state.renameRollback[id];
      delete state.renameRollback[id];
      if (!previous) {
        return;
      }
      if (previous.name === undefined) {
        delete state.nameByProject[id];
      } else {
        state.nameByProject[id] = previous.name;
      }
      if (previous.options) {
        state.filenameOptionsByProject[id] = { ...previous.options };
      } else {
        delete state.filenameOptionsByProject[id];
      }
      const asset = state.combinedByProject[id];
      if (asset && previous.assetName !== undefined) {
        asset.name = previous.assetName;
      }
    })
    .addCase(DELETE_PROJECT_SUCCESS, (state, action) => {
      // EVICT. Nothing reuses a project id, so holding its membership, name
      // and merged asset after it is gone is not wrong — it just makes the
      // slice's size a function of how much browsing happened rather than of
      // what exists, and leaves a deleted document's asset reachable by a
      // selector that has no reason to expect one.
      const id = String(action.payload.projectTdoId);
      delete state.membersByProject[id];
      delete state.combinedByProject[id];
      delete state.combinedLoadFailed[id];
      delete state.batesByProject[id];
      delete state.batesRollback[id];
      delete state.redactionByProject[id];
      delete state.redactionWrite[id];
      delete state.filenameOptionsByProject[id];
      delete state.renameRollback[id];
      delete state.nameByProject[id];
      delete state.loading[id];
      delete state.saving[id];
      delete state.combining[id];
      delete state.combiningVersion[id];
    })
    .addCase(SET_PROJECT_BATES_REQUEST, (state, action) => {
      // OPTIMISTIC: the panel closes on Done and the write is a round trip, so
      // the page would otherwise draw the old stamp until it landed.
      //
      // STASHED HERE, not reconstructed in the saga. The saga ran after this
      // and rebuilt "previous" from the record it had just written, which
      // carried the NEW settings at the OLD version — so a failed write left
      // the client drawing a stamp the server never stored, at a version that
      // compared equal to it. Same slot the rename rollback uses.
      const id = action.payload.projectTdoId;
      const previous = state.batesByProject[id];
      // AN UNCHANGED COMMIT IS NOT A CHANGE, and on a SET it is expensive to
      // pretend otherwise. The panel applies on Done alone, so reopening it
      // and pressing Done — or editing a field and putting it back — would
      // otherwise stamp a new instant, and every member's output would read
      // behind and offer a rebuild of the whole release that produces
      // byte-identical PDFs.
      //
      // Same guard and same comparison as the per-file `SET_BATES_STAMP`,
      // which no-ops for the same reason. Both sides have been through
      // `clampBatesSettings`, so they are built the same way and key order
      // is stable.
      //
      // THIS GUARD IS ABOUT THE SCREEN, not about the write. It stops an
      // unchanged commit from showing a new stamp; whether anything is
      // STORED is decided against the stored body, in
      // `writeProjectBatesIntoRedactionAsset`. The two are not interchangeable
      // and the write's cannot be moved here: this reducer has already
      // overwritten the only copy a handler could compare against.
      //
      // TODO: a persisted content HASH would generalise this. Comparing here
      // only catches a write identical to what is stored; a hash is identity
      // BY content, so it would also read two convergent writes as equal —
      // A sets prefix X, B sets Y, C sets X back, and outputs burned under
      // A would still read current. It would also remove the clock entirely.
      // Not done now because it buys a rare case and costs a canonical
      // serializer as a correctness dependency: get key order or an optional
      // field wrong and identical settings hash differently, which is silent
      // false staleness. Watermarks would need the same treatment over their
      // stored form to avoid two identity models in one body.
      if (isSameBatesSettings(previous?.settings, action.payload.settings)) {
        return;
      }
      state.batesRollback[id] = {
        previous: previous ? { ...previous } : previous,
      };
      state.batesByProject[id] = {
        // STAMPED, not counted. A counter derived from what this session last
        // read restarts at a colliding value after a failed parse, and two
        // reviewers both on v3 mint two different v4s — an output burned
        // under one then compares equal to the other. An instant does
        // neither. Only ever tested for equality, so the clock that minted
        // it does not matter.
        modifiedDateTime: new Date().toISOString(),
        settings: { ...action.payload.settings },
      };
    })
    .addCase(CLEAR_PROJECT_BATES_REQUEST, (state, action) => {
      const id = action.payload.projectTdoId;
      const previous = state.batesByProject[id];
      state.batesRollback[id] = {
        previous: previous ? { ...previous } : previous,
      };
      state.batesByProject[id] = null;
    })
    .addCase(SET_PROJECT_BATES_SUCCESS, (state, action) => {
      // Last write wins: the mutation returns only an id, so this is what was
      // SENT rather than what the server holds. Two reviewers applying at once
      // both believe they are current. Acceptable for a single-reviewer
      // workflow and stated rather than implied.
      delete state.batesRollback[action.payload.projectTdoId];
      state.batesByProject[action.payload.projectTdoId] =
        action.payload.batesStamp;
    })
    .addCase(SET_PROJECT_BATES_FAILURE, (state, action) => {
      const id = action.payload.projectTdoId;
      const stashed = state.batesRollback[id];
      if (!stashed) {
        return;
      }
      delete state.batesRollback[id];
      // BACK TO WHAT THE REDUCER SAW, including "never looked up". Writing
      // `null` for that case would turn a failed write into a standing claim
      // that the set carries no stamping. Same shape as RENAME_PROJECT_FAILURE.
      if (stashed.previous === undefined) {
        delete state.batesByProject[id];
      } else {
        state.batesByProject[id] = stashed.previous;
      }
    })
    .addCase(CLEAR_PROJECT_BATES_SUCCESS, (state, action) => {
      delete state.batesRollback[action.payload.projectTdoId];
    })
    .addCase(LOAD_PROJECT_REDACTION_SUCCESS, (state, action) => {
      const { projectTdoId, data, artworkFailures } = action.payload;
      // `none` is a READ answer, distinct from the absent key that reads as
      // `unread`. A project with no record and a project nobody has asked
      // about are different facts about the same empty-looking state.
      // Cast for the same reason `combinedByProject` needs one below: Immer's
      // draft type cannot hold the record's readonly `watermarks` array. The
      // record is a snapshot the reducer only ever stores whole — nothing
      // mutates it in place — so this costs no safety the state did not
      // already have.
      state.redactionByProject[projectTdoId] = (
        data
          ? {
              status: 'loaded',
              data,
              ...(artworkFailures?.length ? { artworkFailures } : {}),
            }
          : { status: 'none' }
      ) as (typeof state.redactionByProject)[string];
    })
    .addCase(SET_PROJECT_WATERMARKS_REQUEST, (state, action) => {
      // OPTIMISTIC: Apply closes the panel and the write is a round trip, so
      // the page would otherwise redraw the old marks until it landed.
      //
      // STASHED HERE, not reconstructed in the saga — by the time the saga
      // runs, the previous record is gone from the slice. Same slot discipline
      // as the rename and Bates rollbacks.
      const { projectTdoId, watermarks, watermarksOff } = action.payload;
      const previous = state.redactionByProject[projectTdoId];
      // One assignment for both halves: entering `saving` IS clearing the
      // last attempt's verdict, which the two-map version had to remember to
      // do separately. The slot always describes the NEWEST write, which is
      // the one whose record is on screen.
      state.redactionWrite[projectTdoId] = {
        status: 'saving',
        writeId: action.payload.writeId,
        rollback: previous ?? { status: 'unread' },
      };
      // Bumped from a LOADED record only. There is nothing to carry forward
      // from `none`, and the panel does not open on `unread` or `failed` — so
      // a write from either of those starts the counter rather than guessing
      // where it had got to.

      state.redactionByProject[projectTdoId] = {
        status: 'loaded',
        data: {
          // No asset exists until the write lands.
          assetId: null,
          projectTdoId: null,
          // THIS write is when the watermarks changed, so it stamps them.
          // Nothing is carried forward from the previous record and nothing
          // is counted — see the Bates bump above for why.
          watermarksModifiedDateTime: new Date().toISOString(),
          watermarks,
          watermarksOff,
          // Bates is NOT touched here, so its stamp and settings carry
          // forward unchanged. That is the whole point of a stamp per
          // concern: a watermark edit must not stale every member's
          // numbering, nor a Bates edit their marks.
          batesModifiedDateTime:
            previous?.status === 'loaded'
              ? previous.data.batesModifiedDateTime
              : null,
          bates: previous?.status === 'loaded' ? previous.data.bates : null,
        },
      } as (typeof state.redactionByProject)[string];
    })
    .addCase(SET_PROJECT_WATERMARKS_SUCCESS, (state, action) => {
      const id = action.payload.projectTdoId;
      const inFlight = state.redactionWrite[id];
      if (
        inFlight?.status === 'saving' &&
        inFlight.writeId !== action.payload.writeId
      ) {
        // A SUPERSEDED WRITE LANDED. The record on screen belongs to the
        // newer write and must not be dragged back to this one's content —
        // that would discard the reviewer's second apply silently. But what
        // we would roll back TO has changed: the server now holds this, ids
        // and all, so the stash is updated in place rather than left
        // pointing at an optimistic record that never existed anywhere.
        state.redactionWrite[id] = {
          ...inFlight,
          rollback: {
            status: 'loaded',
            data: action.payload.data,
          },
        } as (typeof state.redactionWrite)[string];
        return;
      }
      // Last write wins: the asset write returns its own id, so this is what
      // was SENT rather than a re-read of what the server now holds. Two
      // reviewers applying at once both believe they are current — stated
      // rather than implied, and detectable later because the superseded
      // asset survives.
      delete state.redactionWrite[id];
      state.redactionByProject[id] = {
        status: 'loaded',
        data: action.payload.data,
      } as (typeof state.redactionByProject)[string];
    })
    .addCase(SET_PROJECT_WATERMARKS_FAILURE, (state, action) => {
      const id = action.payload.projectTdoId;
      const inFlight = state.redactionWrite[id];
      if (inFlight?.status !== 'saving') {
        return;
      }
      if (inFlight.writeId !== action.payload.writeId) {
        // A SUPERSEDED WRITE FAILED. The newer one owns the record and the
        // slot, and its own result decides what happens to them; rolling back
        // here would discard edits the reviewer has not been told anything
        // about. The saga still raises the snackbar, so the failure is said.
        return;
      }
      const stashed = inFlight.rollback;
      state.redactionWrite[id] = { status: 'failed' };
      // BACK TO WHAT THE REDUCER SAW, including "never looked up" — which is a
      // value here rather than an absent key, so putting it back is an
      // assignment. The Bates slot needs an envelope to express this.
      state.redactionByProject[id] = stashed;
    })
    .addCase(LOAD_PROJECT_REDACTION_FAILURE, (state, action) => {
      // RECORDED, not left absent. An unrecorded failure is indistinguishable
      // from "not asked yet", so every reader waits forever for a reply that
      // already came back — which is what LOAD_COMBINED_FAILURE does to the
      // Bates record.
      state.redactionByProject[action.payload.projectTdoId] = {
        status: 'failed',
      };
    })
    .addCase(LOAD_COMBINED_REQUEST, (state, action) => {
      // A retry clears the last verdict, so the page shows the spinner again
      // rather than the error it is trying to get past.
      delete state.combinedLoadFailed[action.payload.projectTdoId];
    })
    .addCase(LOAD_COMBINED_FAILURE, (state, action) => {
      // THE THIRD ANSWER. `undefined` means "not asked" and `null` means
      // "asked, never built"; neither is true after a failed read, and leaving
      // the entry absent made the tab wait forever for a reply that had
      // already come back. The cached asset, if there is one, is LEFT in place
      // — a failed refresh is not evidence the document went away.
      state.combinedLoadFailed[action.payload.projectTdoId] = true;
    })
    .addCase(LOAD_COMBINED_SUCCESS, (state, action) => {
      delete state.combinedLoadFailed[action.payload.projectTdoId];
      if (action.payload.projectName) {
        state.nameByProject[action.payload.projectTdoId] =
          action.payload.projectName;
      }
      // Immer's draft type cannot hold the summary's readonly `members` array.
      // The summary is a snapshot the reducer only ever stores whole — nothing
      // mutates it in place — so the cast costs no safety the state did not
      // already have.
      if (action.payload.filenameOptions) {
        state.filenameOptionsByProject[action.payload.projectTdoId] = {
          ...action.payload.filenameOptions,
        };
      }
      if (action.payload.batesStamp !== undefined) {
        state.batesByProject[action.payload.projectTdoId] =
          action.payload.batesStamp;
      }
      state.combinedByProject[action.payload.projectTdoId] = action.payload
        .asset as Draft<CombinedAssetSummary> | null;
    })
);

export default projectsReducer;
